import React from 'react';
import Icon from './Icon';

// The list-page filter toolbar, shared by every admin table.
//
// Each page had its own copy laid out as `justifyContent: space-between`
// across every child, which spread the search box, the selects and the count
// to the far corners of the card with canyons between them. Filters belong in
// one cluster on the left; only the count is pushed right.
//
// Usage:
//   <FilterBar right={<CountPill count={total} noun="blog" />}>
//     <SearchField value={q} onChange={setQ} placeholder="Search blogs..." />
//     <FilterSelect value={cat} onChange={setCat}>…</FilterSelect>
//   </FilterBar>

const styles = {
  bar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 16px', borderBottom: '1px solid #e2e8f0',
    gap: '12px', flexWrap: 'wrap',
  },
  group: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 },
  searchWrap: { position: 'relative', display: 'flex', alignItems: 'center', minWidth: '200px' },
  searchIcon: { position: 'absolute', left: '12px', color: '#94a3b8', display: 'flex', pointerEvents: 'none' },
  searchInput: {
    width: '100%', padding: '10px 32px 10px 34px', border: '1px solid #e2e8f0',
    borderRadius: '8px', fontSize: '14px', outline: 'none', color: '#1e293b',
  },
  searchClear: {
    position: 'absolute', right: '8px', width: '20px', height: '20px',
    border: 'none', borderRadius: '50%', backgroundColor: 'transparent',
    color: '#94a3b8', cursor: 'pointer', padding: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  select: {
    padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px',
    fontSize: '14px', outline: 'none', backgroundColor: '#fff',
    color: '#475569', cursor: 'pointer',
  },
  // Every control that is actively narrowing the table shares one accent, so
  // an unexpectedly short list is easy to explain at a glance.
  selectActive: { borderColor: '#c7d2fe', backgroundColor: '#eef2ff', color: '#4338ca' },
  reset: {
    border: 'none', backgroundColor: 'transparent', color: '#4f46e5',
    fontSize: '13px', fontWeight: '500', cursor: 'pointer',
    padding: '6px 4px', fontFamily: 'inherit',
  },
  pill: {
    fontSize: '13px', color: '#64748b', padding: '6px 12px',
    backgroundColor: '#f1f5f9', borderRadius: '8px', fontWeight: '500',
    whiteSpace: 'nowrap', flexShrink: 0,
  },
  pillActive: { color: '#4338ca', backgroundColor: '#eef2ff' },
  pillTotal: { color: '#94a3b8', fontWeight: '400' },
};

export function FilterBar({ children, right, style }) {
  return (
    <div style={{ ...styles.bar, ...style }}>
      <div style={styles.group}>{children}</div>
      {right}
    </div>
  );
}

// `onChange` receives the string value, not the event — every call site wanted
// the value and none of them wanted the event.
export function SearchField({ value, onChange, placeholder = 'Search...', width = 300 }) {
  return (
    <div style={{ ...styles.searchWrap, flex: `0 1 ${width}px` }}>
      <span style={styles.searchIcon}>
        <Icon name="search" size={15} />
      </span>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={styles.searchInput}
      />
      {value && (
        <button type="button" aria-label="Clear search" onClick={() => onChange('')} style={styles.searchClear}>
          <Icon name="close" size={12} />
        </button>
      )}
    </div>
  );
}

export function FilterSelect({ value, onChange, children, ...rest }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...styles.select, ...(value ? styles.selectActive : null) }}
      {...rest}
    >
      {children}
    </select>
  );
}

export function ResetButton({ onClick, label = 'Reset' }) {
  return (
    <button type="button" onClick={onClick} style={styles.reset}>{label}</button>
  );
}

// `total` is the unfiltered size. Passing it renders "12,774 of 13,408", so a
// filtered count is never mistaken for the size of the table.
export function CountPill({ count = 0, total, noun = 'item', nounPlural, style }) {
  const filtered = typeof total === 'number' && total !== count;
  const word = count === 1 ? noun : (nounPlural || `${noun}s`);
  return (
    <span style={{ ...styles.pill, ...(filtered ? styles.pillActive : null), ...style }}>
      {count.toLocaleString()}
      {filtered && <span style={styles.pillTotal}> of {total.toLocaleString()}</span>}
      {' '}{word}
    </span>
  );
}

export default FilterBar;
